// @ts-check
import { createBrowserRouter, createRoutesFromElements, Navigate, Route, RouterProvider } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import RouteErrorFallback from './components/RouteErrorFallback';
import { ThemeProvider } from './context/ThemeContext';
import { ToastProvider } from './context/ToastContext';
import AdminLayout from './pages/AdminLayout';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminRegisterPage from './pages/AdminRegisterPage';
import AnalyticsDashboardPage from './pages/AnalyticsDashboardPage';
import AtelierAccountsPage from './pages/AtelierAccountsPage';
import AtelierCrmPage from './pages/AtelierCrmPage';
import AtelierProjectDetailPage from './pages/AtelierProjectDetailPage';
import AtelierProjectsPage from './pages/AtelierProjectsPage';
import AtelierProspectDetailPage from './pages/AtelierProspectDetailPage';
import AtelierSettingsPage from './pages/AtelierSettingsPage';
import BillingSettingsPage from './pages/BillingSettingsPage';
import CatalogSettingsPage from './pages/CatalogSettingsPage';
import CollectionsPageNew from './pages/CollectionsPageNew';
import CommentManagementPage from './pages/CommentManagementPage';
import ContactSubmissionsPage from './pages/ContactSubmissionsPage';
import CreateVendorOrderPage from './pages/CreateVendorOrderPage';
import CustomersPage from './pages/CustomersPage';
import DashboardPage from './pages/DashboardPage';
import DiscountsPage from './pages/DiscountsPage';
import DressTrackingPage from './pages/DressTrackingPage';
import EmailTemplatesPage from './pages/EmailTemplatesPage';
import ExpensesPage from './pages/ExpensesPage';
import ExpenseTrackerPage from './pages/ExpenseTrackerPage';
import HRTrackerPage from './pages/HRTrackerPage';
import InfluencersPage from './pages/InfluencersPage';
import InstagramFeedCurationPage from './pages/InstagramFeedCurationPage';
import InventoryPage from './pages/InventoryPage';
import InvoicesPage from './pages/InvoicesPage';
import JobTrackerPage from './pages/JobTrackerPage';
import LiveMapPage from './pages/LiveMapPage';
import MaterialsPage from './pages/MaterialsPage';
import MyTasksPage from './pages/MyTasksPage';
import OrdersPage from './pages/OrdersPage';
import PostManagementPage from './pages/PostManagementPage';
import PricingConfigPage from './pages/PricingConfigPage';
import ProductMaterialsPage from './pages/ProductMaterialsPage';
import ProductsPage from './pages/ProductsPage';
import RecoveryPage from './pages/RecoveryPage';
import ReviewsPage from './pages/ReviewsPage';
import SalesEventsPage from './pages/SalesEventsPage';
import SeoPage from './pages/SeoPage';
import ShipmentsPage from './pages/ShipmentsPage';
import ShippingPartnersPage from './pages/ShippingPartnersPage';
import ShowcasePage from './pages/ShowcasePage';
import SiteContentPage from './pages/SiteContentPage';
import SiteSettingsPageNew from './pages/SiteSettingsPageNew';
import SizeGuidesPage from './pages/SizeGuidesPage';
import StagingSyncPage from './pages/StagingSyncPage';
import TaskKanbanPage from './pages/TaskKanbanPage';
import TasksPage from './pages/TasksPage';
import TaxonomyPage from './pages/TaxonomyPage';
import TeamWorkloadPage from './pages/TeamWorkloadPage';
import UserRolesPage from './pages/UserRolesPage';
import VendorDetailPage from './pages/VendorDetailPage';
import VendorOrderDetailPage from './pages/VendorOrderDetailPage';
import VendorOrdersPage from './pages/VendorOrdersPage';
import VendorsPage from './pages/VendorsPage';
import WhatsAppPage from './pages/WhatsAppPage';
import WholesalePage from './pages/WholesalePage';
import WholesalePartnerPage from './pages/WholesalePartnerPage';

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route errorElement={<RouteErrorFallback />}>
      <Route path="/login" element={<AdminLoginPage />} />
      <Route path="/register" element={<AdminRegisterPage />} />

      <Route
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/collections" element={<CollectionsPageNew />} />
        <Route path="/inventory" element={<InventoryPage />} />
        <Route path="/orders" element={<OrdersPage />} />
        <Route path="/sales-events" element={<SalesEventsPage />} />
        <Route path="/invoices" element={<InvoicesPage />} />
        <Route path="/wholesale" element={<WholesalePage />} />
        <Route path="/wholesale/:partnerId" element={<WholesalePartnerPage />} />
        <Route path="/billing" element={<BillingSettingsPage />} />
        <Route path="/shipments" element={<ShipmentsPage />} />
        <Route path="/shipping-partners" element={<ShippingPartnersPage />} />
        <Route path="/materials" element={<MaterialsPage />} />
        <Route path="/products/:productId/materials" element={<ProductMaterialsPage />} />
        <Route path="/contact-submissions" element={<ContactSubmissionsPage />} />
        <Route path="/site-content" element={<SiteContentPage />} />
        <Route path="/settings" element={<SiteSettingsPageNew />} />
        <Route path="/settings/catalog" element={<CatalogSettingsPage />} />
        <Route path="/settings/pricing" element={<PricingConfigPage />} />
        <Route path="/settings/taxonomy" element={<TaxonomyPage />} />
        <Route path="/settings/size-guides" element={<SizeGuidesPage />} />
        <Route path="/email-templates" element={<EmailTemplatesPage />} />
        <Route path="/user-roles" element={<UserRolesPage />} />
        <Route path="/discounts" element={<DiscountsPage />} />
        <Route path="/customers" element={<CustomersPage />} />
        <Route path="/whatsapp" element={<WhatsAppPage />} />
        <Route path="/analytics" element={<AnalyticsDashboardPage />} />
        <Route path="/seo" element={<SeoPage />} />
        <Route path="/reviews" element={<ReviewsPage />} />
        <Route path="/expense-tracker" element={<ExpenseTrackerPage />} />
        <Route path="/job-tracker" element={<JobTrackerPage />} />
        <Route path="/hr-tracker" element={<HRTrackerPage />} />
        <Route path="/vendors" element={<VendorsPage />} />
        <Route path="/vendors/orders" element={<VendorOrdersPage />} />
        <Route path="/vendors/orders/create" element={<CreateVendorOrderPage />} />
        <Route path="/vendors/orders/:orderId" element={<VendorOrderDetailPage />} />
        <Route path="/vendors/:vendorId" element={<VendorDetailPage />} />
        <Route path="/expenses" element={<ExpensesPage />} />
        <Route path="/recovery" element={<RecoveryPage />} />
        <Route path="/tasks" element={<TasksPage />} />
        <Route path="/tasks/kanban" element={<TaskKanbanPage />} />
        <Route path="/tasks/my-tasks" element={<MyTasksPage />} />
        <Route path="/tasks/workload" element={<TeamWorkloadPage />} />
        <Route path="/influencers" element={<InfluencersPage />} />
        <Route path="/marketing/dresses" element={<DressTrackingPage />} />
        <Route path="/marketing/comments" element={<CommentManagementPage />} />
        <Route path="/marketing/posts" element={<PostManagementPage />} />
        <Route path="/marketing/feed" element={<InstagramFeedCurationPage />} />
        <Route path="/marketing/showcase" element={<ShowcasePage />} />
        <Route path="/live-map" element={<LiveMapPage />} />
        <Route path="/atelier/crm" element={<AtelierCrmPage />} />
        <Route path="/atelier/crm/:prospectId" element={<AtelierProspectDetailPage />} />
        <Route path="/atelier/accounts" element={<AtelierAccountsPage />} />
        <Route path="/atelier/projects" element={<AtelierProjectsPage />} />
        <Route path="/atelier/projects/:projectId" element={<AtelierProjectDetailPage />} />
        <Route path="/atelier/settings" element={<AtelierSettingsPage />} />
        <Route path="/developer/staging-sync" element={<StagingSyncPage />} />
      </Route>

      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Route>,
  ),
);

function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <ErrorBoundary>
          <RouterProvider router={router} />
        </ErrorBoundary>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
